const libPictView = require('pict-view');

const _ViewConfiguration = require('./panes/HeadlightPanels-Pane-View-Templates.json')['HeadlightPanels-Pane-SettingsPaneHtml-View'];

/** @typedef {import('./HeadlightPanels-View.js').Pict} Pict */

class HeadlightPaneSettings extends libPictView
{
	constructor(pFable, pOptions, pServiceHash)
	{
		const tmpOptions = Object.assign({}, _ViewConfiguration, pOptions);
		super(pFable, tmpOptions, pServiceHash);

		/** @type {Pict} */
		this.pict;

		this.getLocallyStoredTheme();
	}

	/**
	 * @param {import('pict-view').ErrorCallback} fCallback - The callback function to call after rendering
	 * @param {import('pict-view').Renderable} pRenderable - The renderable object to render
	 */
	onAfterRenderAsync(fCallback, pRenderable)
	{
		const tmpPanelSettings = this.AppData.CurrentUser?.Settings;
		if (!tmpPanelSettings || !tmpPanelSettings.HeadlightPanels)
		{
			// if we don't have a user, we can't save their preferences
			this.log.warn('No user settings found to load the user preferences.');
		}
		else
		{
			for (const [ key, value ] of Object.entries(tmpPanelSettings.HeadlightPanels))
			{
				if (value)
				{
					const setting = document.getElementById(`setting-${key}`) ||
						document.getElementById(`setting-${key}-${value}`);
					if (setting)
					{
						setting.classList.add('is-active');
					}
				}
			}
		}
		this.syncAutoSaveSettings();
		super.onAfterRenderAsync(fCallback, pRenderable);
	}

	/**
	 * Resolve the shared auto-save provider, if this application has one.
	 * @return {any}
	 */
	get autoSaveProvider()
	{
		return this.pict.providers?.AutoSave;
	}

	/**
	 * Paint the auto-save rows from the provider's effective state.
	 *
	 * Called on every render of this pane, because the pane is built on demand and the state it shows
	 * is a blend of the form manifest and the user's stored preference.
	 * @return {void}
	 */
	syncAutoSaveSettings()
	{
		const tmpToggle = document.getElementById('setting-AutoSave');
		const tmpIntervalBlock = document.getElementById('setting-AutoSaveIntervalSeconds');
		const tmpNote = document.getElementById('setting-AutoSave-Note');
		if (!tmpToggle || !tmpIntervalBlock || !tmpNote)
		{
			return;
		}
		const tmpState = this.autoSaveProvider?.getUserFacingState?.();
		// No auto-save provider, or an application with no form to save: hide the controls rather than
		// offering a preference that could not do anything.
		if (!tmpState || !tmpState.Available)
		{
			tmpToggle.classList.add('is-hidden');
			tmpIntervalBlock.classList.add('is-hidden');
			tmpNote.classList.add('is-hidden');
			return;
		}
		tmpToggle.classList.remove('is-hidden');
		tmpToggle.classList.toggle('is-active', tmpState.Enabled);
		// onAfterRenderAsync's generic preference sweep stamps is-active on any `setting-<Key>` element
		// whose stored value is truthy, which catches the interval block because the cadence is a number.
		// It holds an input, not a toggle, so clear it.
		tmpIntervalBlock.classList.remove('is-active');

		// Locked means the user cannot change it from here — the form manifest opted out, or the document
		// belongs to another customer — so the row goes inert. A reason without a lock (impersonation,
		// which only moves the default) still explains itself but leaves the toggle live.
		tmpToggle.classList.toggle('is-disabled', tmpState.Locked);
		tmpNote.classList.toggle('is-hidden', !tmpState.Reason);
		if (tmpState.Reason)
		{
			tmpNote.textContent = tmpState.Reason;
		}

		// The cadence only means anything while auto-save is on.
		tmpIntervalBlock.classList.toggle('is-hidden', !tmpState.Enabled || tmpState.Locked);

		const tmpInput = /** @type {HTMLInputElement} */ (document.getElementById('setting-AutoSaveIntervalSeconds-Input'));
		if (tmpInput)
		{
			tmpInput.min = String(tmpState.MinimumIntervalSeconds);
			tmpInput.max = String(tmpState.MaximumIntervalSeconds);
			tmpInput.value = String(tmpState.IntervalSeconds);
		}
	}

	/**
	 * Turn auto-save on or off for this user, across every form they open.
	 * @return {void}
	 */
	toggleAutoSave()
	{
		const tmpState = this.autoSaveProvider?.getUserFacingState?.();
		if (!tmpState || !tmpState.Available || tmpState.Locked)
		{
			return;
		}
		this.updateUserPreferences('AutoSave', !tmpState.Enabled);
		// Take effect on the document already open rather than at the next page load.
		this.autoSaveProvider.reconfigure();
		this.syncAutoSaveSettings();
	}

	/**
	 * Persist the auto-save cadence, in seconds.
	 *
	 * Wired to the field's change event, so it fires once the user leaves the field or presses Enter
	 * rather than on every keystroke. The value is clamped to the range auto-save supports and echoed
	 * back into the field: the min/max attributes only gate the spinner, so a typed-in 5 would otherwise
	 * sit there reading 5 while auto-save quietly ran at 15.
	 *
	 * @param {string|number} pSeconds - The entered interval, in seconds.
	 * @return {void}
	 */
	setAutoSaveInterval(pSeconds)
	{
		const tmpState = this.autoSaveProvider?.getUserFacingState?.();
		const tmpInput = /** @type {HTMLInputElement} */ (document.getElementById('setting-AutoSaveIntervalSeconds-Input'));
		const tmpSeconds = Math.round(Number(pSeconds));
		if (!Number.isFinite(tmpSeconds) || tmpSeconds <= 0)
		{
			this.log.warn(`Ignoring an unusable auto-save interval: ${pSeconds}`);
			// Put the field back to the value that is actually in effect.
			if (tmpInput && tmpState)
			{
				tmpInput.value = String(tmpState.IntervalSeconds);
			}
			return;
		}
		const tmpMinimum = tmpState?.MinimumIntervalSeconds ?? 15;
		const tmpMaximum = tmpState?.MaximumIntervalSeconds ?? 3600;
		const tmpClamped = Math.min(tmpMaximum, Math.max(tmpMinimum, tmpSeconds));
		if (tmpInput)
		{
			tmpInput.value = String(tmpClamped);
		}
		this.updateUserPreferences('AutoSaveIntervalSeconds', tmpClamped);
		// Re-arming restarts the countdown, which is what someone who just changed the cadence expects.
		this.autoSaveProvider?.reconfigure?.();
	}

	/**
	 * Check the user settings for an active chip type that is passed in.
	 * @param {string} pType - The chip type to check for
	 * @return {boolean} - Whether the chip type is active or not
	 * @example
	 * checkChipTypeActive('New');
	 * checkChipTypeActive('ExternalDatabaseIDs');
	 */
	checkChipTypeActive(pType)
	{
		return this.AppData.CurrentUser?.Settings?.HeadlightPanels?.ActiveChipTypes?.includes(pType);
	}

	/**
	 * This function is used to get the user preferences for the headlight panels.
	 * @param {string} pSetting - The setting to get the user preference for
	 * @return {any} - The user preference for the setting
	 * @example
	 * readUserPrefrence('DocumentTheme');
	 * readUserPrefrence('NavBarLocation');
	 */
	readUserPreference(pSetting)
	{
		const tmpPanelSettings = this.AppData.CurrentUser?.Settings;
		if (!tmpPanelSettings)
		{
			// if we don't have a user, we can't save their preferences
			this.log.warn('No user settings found to load the user preferences.');
			return;
		}
		// create the HeadlightPanels object if it doesn't exist
		if (!tmpPanelSettings.hasOwnProperty('HeadlightPanels'))
		{
			this.log.warn('No HeadLightPanel user settings found to load the user preferences.');
			return;
		}
		return this.AppData.CurrentUser?.Settings?.HeadlightPanels?.[pSetting];
	}

	/**
	 * This function is used to render the user preferences for the headlight panels and apply them to the document.
	 * @return {void}
	 * @async
	 */
	/**
	 * Resolve the current user's HeadlightPanels preference bag, creating it if this is their first visit.
	 * @return {Object|null} The preference object, or null when there is no user to read preferences from
	 */
	getUserPanelPreferences()
	{
		// these settings are only applicable in interactive mode
		if (!this.AppData.Interactive)
		{
			return null;
		}
		const tmpPanelSettings = this.AppData.CurrentUser?.Settings;
		if (!tmpPanelSettings)
		{
			return null;
		}
		// create the HeadlightPanels object if it doesn't exist
		if (!tmpPanelSettings.hasOwnProperty('HeadlightPanels'))
		{
			tmpPanelSettings.HeadlightPanels = {};
		}
		return tmpPanelSettings.HeadlightPanels;
	}

	/**
	 * Apply the preferences that decide what the very first paint looks like.
	 *
	 * These come straight off the already-loaded CurrentUser record -- there is no fetch to wait on -- and
	 * the navbar's position is baked into the template at render time. Applying them after the render
	 * means the navbar renders in the default position and then visibly jumps to the user's, taking a
	 * body-class reflow with it; the same goes for the theme and the page width.
	 * @return {void}
	 */
	applyPreRenderUserPreferences()
	{
		const tmpPanelPreferences = this.getUserPanelPreferences();
		if (!tmpPanelPreferences)
		{
			return;
		}
		// if a theme is set, apply it to the document
		if (tmpPanelPreferences.hasOwnProperty('DocumentTheme'))
		{
			this.toggleDocumentColorTheme(tmpPanelPreferences.DocumentTheme, true);
		}
		// if a screen width is set, apply it to the document
		//widescreen, fancy, standard
		if (!tmpPanelPreferences.hasOwnProperty('ScreenWidth'))
		{
			tmpPanelPreferences.ScreenWidth = 'standard';
		}
		this.toggleScreenWidth(tmpPanelPreferences.ScreenWidth, true);
		// set the navbar location based on the user's preference (this moves the navbar to the top or bottom of the screen)
		if (tmpPanelPreferences.NavBarLocation)
		{
			this.toggleNavbarLocation(tmpPanelPreferences.NavBarLocation, true);
		}
	}

	loadUserPreferences()
	{
		const tmpPanelPreferences = this.getUserPanelPreferences();
		if (this.AppData.Interactive && !tmpPanelPreferences)
		{
			// if we don't have a user, we can't save their preferences
			this.log.warn('No user settings found to load the user preferences.');
			return;
		}
		if (!tmpPanelPreferences)
		{
			return;
		}
		// re-applying the first-paint preferences is a no-op; this keeps the call correct on its own
		// for any caller that reaches loadUserPreferences() without the pre-render pass having run
		this.applyPreRenderUserPreferences();
		// the rest need the rendered panel elements, so they can only run after the render
		// set the lefty mode based on the user's preference (this inverts the left and right panel locations)
		if (tmpPanelPreferences.LeftyMode)
		{
			this.toggleLeftyMode(tmpPanelPreferences.LeftyMode, true);
		}
		// handle the state of all active chips based on the user's preference
		if (tmpPanelPreferences.ActiveChipTypes?.length)
		{
			tmpPanelPreferences.ActiveChipTypes.forEach((chip) => this.toggleChipTypeActive(null, chip, true, true));
		}
		// handle the state of the help text based on the user's preference
		if (tmpPanelPreferences.HelpText !== undefined)
		{
			this.toggleHelpText(tmpPanelPreferences.HelpText, true);
		}
	}

	/**
	 * This function is used to toggle the theme of the application between light and dark styling.
	 * This will also store the user's preference in the current user object on the server.
	 * @param {string} pTheme - The theme to set the application to if provided (system, light, dark)
	 * @param {boolean} skipSave - Whether to skip saving the user's preference to the server
	 * @return {void}
	*/
	toggleDocumentColorTheme(pTheme, skipSave = false)
	{
		document.documentElement.setAttribute('data-theme', pTheme);

		// NOTE: This is to clear out the old location of the theme in the user settings
		if (this.AppData?.CurrentUser?.Settings.hasOwnProperty('Theme'))
		{
			delete this.AppData.CurrentUser.Settings.Theme;
		}

		// save the user's preference if we're not skipping the save
		if (skipSave) return;

		this.updateUserPreferences('DocumentTheme', pTheme);
	}

	/**
	 * This function is used to get the theme from localStorage for the application,
	 * and toggle between light, dark and system themes.
	 * @return {void}
	*/
	getLocallyStoredTheme()
	{
		const storedTheme = localStorage.getItem('data-theme');
		const themeIcon = document.getElementById('themeIcon');

		if (!themeIcon) return;
		// remove icon classes on init so correct icon displays on toggle
		themeIcon.classList.remove('fa-desktop');
		themeIcon.classList.remove('fa-sun');
		themeIcon.classList.remove('fa-moon');

		// Apply the theme from localStorage as default
		if (storedTheme)
		{
			document.documentElement.setAttribute('data-theme', storedTheme);
		}
		else
		{
			// if there is no stored theme, default to light theme
			document.documentElement.setAttribute('data-theme', 'light');
			localStorage.setItem('data-theme', 'light');
			themeIcon.classList.add('fa-sun');
		}
		if (storedTheme === 'system')
		{
			// if theme is system add desktop icon
			themeIcon.classList.add('fa-desktop');
			themeIcon.title = 'Change the theme to dark mode.';
		}
		// todo: take out this trash, does checking for && storedTheme is dark defeat the purpose of checking user preference? not sure but this makes it work
		else if ((window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) && storedTheme === 'dark')
		{
			document.documentElement.setAttribute('data-theme', 'dark');
			localStorage.setItem('data-theme', 'dark');
			themeIcon.classList.add('fa-moon');
			themeIcon.title = 'Change the theme to light mode.';
		}
		else
		{
			document.documentElement.setAttribute('data-theme', 'light');
			localStorage.setItem('data-theme', 'light');
			themeIcon.classList.add('fa-sun');
			themeIcon.title = 'Change the theme to system mode.';
		}
	}

	/**
	 * This function is used to get the theme from localStorage for the application,
	 * and toggle between light, dark and system themes.
	 * @return {void}
	*/
	toggleLocallyStoredTheme()
	{
		const themeIcon = document.getElementById('themeIcon');

		if (!themeIcon) return;

		if (themeIcon.classList.contains('fa-moon'))
		{
			themeIcon.classList.remove('fa-moon');
			themeIcon.classList.add('fa-sun');
			themeIcon.title = 'Change the theme to system mode.';
			localStorage.setItem('data-theme', 'light');
			document.documentElement.setAttribute('data-theme', 'light');
		}
		else if (themeIcon.classList.contains('fa-sun'))
		{
			themeIcon.classList.remove('fa-sun');
			themeIcon.classList.add('fa-desktop');
			themeIcon.title = 'Change the theme to dark mode.';
			localStorage.setItem('data-theme', 'system');
			document.documentElement.setAttribute('data-theme', 'system');
		}
		else
		{
			themeIcon.classList.remove('fa-desktop');
			themeIcon.classList.add('fa-moon');
			themeIcon.title = 'Change the theme to light mode.';
			localStorage.setItem('data-theme', 'dark');
			document.documentElement.setAttribute('data-theme', 'dark');
		}
	}

	/**
	 * This function is used to toggle the help text visibility on the document.
	 * @param {boolean} pHelpText - The value to set the help text to if provided
	 * @param {boolean} skipSave - Whether to skip saving the user's preference to the server
	 * @return {void}
	 */
	toggleHelpText(pHelpText, skipSave = false)
	{
		if (pHelpText === undefined)
		{
			this.pict.settings.HeadlightPanels.Configuration.PanelOptions.useHelpText = !this.pict.settings.HeadlightPanels.Configuration.PanelOptions.useHelpText;
		}
		else
		{
			this.pict.settings.HeadlightPanels.Configuration.PanelOptions.useHelpText = pHelpText;
		}
		// handle the help text visibility on the document
		if (this.pict.settings.HeadlightPanels.Configuration.PanelOptions.useHelpText)
		{
			document.body.classList.remove('has-hidden-help-text');
		}
		else
		{
			document.body.classList.add('has-hidden-help-text');
		}

		// save the user's preference if we're not skipping the save
		if (skipSave) return;

		this.updateUserPreferences('HelpText', this.pict.settings.HeadlightPanels.Configuration.PanelOptions.useHelpText);
	}

	/**
	 * This function is used to toggle between widescreen or standard for all documents.
	 * @param {boolean} pWidth - The value to set the document width if provided
	 * @param {boolean} skipSave - Whether to skip saving the user's preference to the server
	 * @return {void}
	 */
	//TODO: add a 3rd option for "fancy" that is user defined width
	toggleScreenWidth(pWidth, skipSave = false)
	{
		if (pWidth === undefined)
		{
			this.pict.settings.HeadlightPanels.Configuration.ScreenWidth = 'standard';
		}
		else
		{
			this.pict.settings.HeadlightPanels.Configuration.ScreenWidth = pWidth;
		}
		// apply the widescreen to the document if one desktop
		const isPhoneDevice = window.matchMedia && window.matchMedia('(max-width: 768px)').matches;
		const alwaysWideScreenModeClass = 'always-widescreen-mode';
		// set view to default width and icon to standard (it wont be displayed
		// else icon to minimize if widescreen on desktop, else set to standard
		if (isPhoneDevice)
		{
			document.body.classList.remove(alwaysWideScreenModeClass);
			this.pict.providers.DocumentProvider.setScreenResizeIcon('standard');
		}
		else
		{
			if (this.pict.settings.HeadlightPanels.Configuration.ScreenWidth === 'widescreen')
			{
				document.body.classList.add(alwaysWideScreenModeClass);
			}
			else
			{
				document.body.classList.remove(alwaysWideScreenModeClass);
			}
			this.pict.providers.DocumentProvider.setScreenResizeIcon(this.pict.settings.HeadlightPanels.Configuration.ScreenWidth);
		}

		// save the user's preference if we're not skipping the save
		if (skipSave) return;

		this.updateUserPreferences('ScreenWidth', this.pict.settings.HeadlightPanels.Configuration.ScreenWidth);
	}

	/**
	 * This function is used to toggle "lefty mode" for the application. This inverts all
	 * the panels to the opposite side of the screen for left-handed users.
	 * @param {boolean} pUseLeftyMode - The value to set the lefty mode to if provided
	 * @param {boolean} skipSave - Whether to skip saving the user's preference to the server
	 * @return {void}
	 */
	toggleLeftyMode(pUseLeftyMode, skipSave = false)
	{
		if (pUseLeftyMode === undefined)
		{
			this.pict.settings.HeadlightPanels.Configuration.PanelOptions.useLeftyMode = !this.pict.settings.HeadlightPanels.Configuration.PanelOptions.useLeftyMode;
		}
		else
		{
			this.pict.settings.HeadlightPanels.Configuration.PanelOptions.useLeftyMode = pUseLeftyMode;
		}
		// hide the panels from jutting out on the opposite side of the screen from the switch
		if (this.pict.settings.HeadlightPanels.Configuration.PanelOptions.useLeftyMode)
		{
			document.body.classList.add('use-lefty-mode');
			// animate the panels to the opposite side of the screen
			document.getElementById('HeadlightPanels-LeftPanel').classList.remove('animate__slideOutLeft');
			document.getElementById('HeadlightPanels-RightPanel').classList.remove('animate__slideOutRight');
			document.getElementById('HeadlightPanels-LeftPanel').classList.add('animate__slideOutRight');
			document.getElementById('HeadlightPanels-RightPanel').classList.add('animate__slideOutLeft');
		}
		else
		{
			document.body.classList.remove('use-lefty-mode');
			// remove old classes and reset back to the default side of the screen
			document.getElementById('HeadlightPanels-LeftPanel').classList.remove('animate__slideOutRight');
			document.getElementById('HeadlightPanels-RightPanel').classList.remove('animate__slideOutLeft');
			document.getElementById('HeadlightPanels-LeftPanel').classList.add('animate__slideOutLeft');
			document.getElementById('HeadlightPanels-RightPanel').classList.add('animate__slideOutRight');
		}
		// close any open panels to get us back to a clean slate
		this.pict.views.HeadlightPanels.closeAllUnpinnedPanels();

		// save the user's preference if we're not skipping the save
		if (skipSave) return;

		this.updateUserPreferences('LeftyMode', this.pict.settings.HeadlightPanels.Configuration.PanelOptions.useLeftyMode);
	}

	/**
	 * This function is used to toggle the location of the navbar between top and bottom.
	 * @param {string} pLocation - The location to set the navbar to if provided (top, bottom)
	 * @param {boolean} skipSave - Whether to skip saving the user's preference to the server
	 * @return {void}
	 */
	toggleNavbarLocation(pLocation, skipSave = false)
	{
		let newLocation = '';
		if (!pLocation)
		{
			switch (this.pict.settings.HeadlightPanels.Configuration.NavBar.location)
			{
			case 'top':
				newLocation = 'bottom';
				break;
			case 'bottom':
				newLocation = 'top';
				break;
			default:
				newLocation = 'bottom';
			}
		}
		else
		{
			newLocation = pLocation;
		}
		// update the navbar location classes on the DOM
		// (the navbar element does not exist yet when this runs ahead of the first render -- the
		// configuration update below is what puts it in the right place to begin with)
		const navbar = document.getElementById('HeadlightPanels-NavBar');
		if (navbar)
		{
			navbar.classList.remove('is-fixed-' + this.pict.settings.HeadlightPanels.Configuration.NavBar.location);
			navbar.classList.add('is-fixed-' + newLocation);
		}
		document.body.classList.remove('has-navbar-fixed-' + this.pict.settings.HeadlightPanels.Configuration.NavBar.location);
		document.body.classList.add('has-navbar-fixed-' + newLocation);
		// update our configuration
		this.pict.settings.HeadlightPanels.Configuration.NavBar.location = newLocation;

		// save the user's preference if we're not skipping the save
		if (skipSave) return;

		this.updateUserPreferences('NavBarLocation', newLocation);
	}

	/**
	 * This function is used to toggle the visiblity of the a specific label type on the document.
	 * @param {object} event - The event object that triggered the function
	 * @param {string} pChip - The chip type to toggle the visibility of
	 * @param {boolean} pIsActive - The value to set the chip type to if provided
	 * @param {boolean} skipSave - Whether to skip saving the user's preference to the server
	 * @return {void}
	 * @example
	 * toggleChipTypeActive('ExternalDatabaseIDs', false);
	 * toggleChipTypeActive('New');
	 */
	toggleChipTypeActive(event, pChip, pIsActive, skipSave = false)
	{
		// if an event was passed in, prevent the default behavior (like clicking a link or toggling date picker)
		if (event)
		{
			event.preventDefault();
			event.stopPropagation();
		}
		// get the user settings object
		const tmpCurrentUserSettings = this.AppData?.CurrentUser?.Settings;
		if (!tmpCurrentUserSettings)
		{
			// if we don't have a user, we can't save their preferences
			this.log.warn('No user settings found to save the chip type visibility.');
			return;
		}
		// make sure the user settings object exists
		if (!tmpCurrentUserSettings.hasOwnProperty('HeadlightPanels'))
		{
			tmpCurrentUserSettings.HeadlightPanels = {};
		}
		if (!tmpCurrentUserSettings.HeadlightPanels.hasOwnProperty('ActiveChipTypes'))
		{
			tmpCurrentUserSettings.HeadlightPanels.ActiveChipTypes = [];
		}
		// if the value is not provided, toggle the chip type
		if (pIsActive === undefined)
		{
			if (tmpCurrentUserSettings?.HeadlightPanels?.ActiveChipTypes?.includes(pChip))
			{
				tmpCurrentUserSettings.HeadlightPanels.ActiveChipTypes = tmpCurrentUserSettings.HeadlightPanels.ActiveChipTypes.filter((chip) => chip !== pChip);
				document.querySelectorAll(`[data-chip-for="${pChip}"]`).forEach((chip) => chip.classList.remove('is-active'));
			}
			else
			{
				tmpCurrentUserSettings.HeadlightPanels.ActiveChipTypes.push(pChip);
				document.querySelectorAll(`[data-chip-for="${pChip}"]`).forEach((chip) => chip.classList.add('is-active'));
			}
		}
		else
		{
			if (pIsActive)
			{
				tmpCurrentUserSettings.HeadlightPanels.ActiveChipTypes.push(pChip);
				document.querySelectorAll(`[data-chip-for="${pChip}"]`).forEach((chip) => chip.classList.add('is-active'));
			}
			else
			{
				tmpCurrentUserSettings.HeadlightPanels.ActiveChipTypes = tmpCurrentUserSettings.HeadlightPanels.ActiveChipTypes.filter((chip) => chip !== pChip);
				document.querySelectorAll(`[data-chip-for="${pChip}"]`).forEach((chip) => chip.classList.remove('is-active'));
			}
		}
		// filter out duplicates
		tmpCurrentUserSettings.HeadlightPanels.ActiveChipTypes = [ ...new Set(tmpCurrentUserSettings.HeadlightPanels.ActiveChipTypes) ];
		// save the user's preference if we're not skipping the save
		if (skipSave) return;
		// tell the server to update (but no need to wait for it to finish)
		this.pict.services.HeadlightDocumentService.updateUserSettings(
			{ HeadlightPanels: { ActiveChipTypes: tmpCurrentUserSettings.HeadlightPanels.ActiveChipTypes } });
	}

	/**
	 * This function is used to bus various settings for the user.
	 *
	 * Booleans and strings additionally paint the matching `setting-<Key>` / `setting-<Key>-<value>`
	 * panel row. Numbers (the auto-save cadence) are stored and persisted but painted by their own
	 * control, since a numeric field has no active/inactive row to toggle.
	 * @param {string} pSetting - The setting to update
	 * @param {boolean|string|number} pValue - The value to set the setting to
	 * @return {void}
	 * @example
	 * updateUserPreferences('LeftyMode', true);
	 * updateUserPreferences('Theme', 'dark');
	 */
	updateUserPreferences(pSetting, pValue)
	{
		const tmpCurrentUserSettings = this.AppData?.CurrentUser?.Settings;
		if (!tmpCurrentUserSettings)
		{
			// if we don't have a user, we can't save their preferences
			this.log.warn('No user settings found to save the user preferences.');
			return;
		}
		if (!tmpCurrentUserSettings.hasOwnProperty('HeadlightPanels'))
		{
			tmpCurrentUserSettings.HeadlightPanels = {};
		}
		if (!tmpCurrentUserSettings.HeadlightPanels.hasOwnProperty(pSetting))
		{
			tmpCurrentUserSettings.HeadlightPanels[pSetting] = false;
		}
		tmpCurrentUserSettings.HeadlightPanels[pSetting] = pValue;
		// tell the server to update (but no need to wait for it to finish). The changed key is passed
		// because an impersonated session saves onto the real user's record and must not carry the
		// impersonated user's other preferences across with it.
		this.pict.services.HeadlightDocumentService.updateUserSettings({ HeadlightPanels: { [pSetting]: pValue } });
		// if the setting is a boolean, toggle the class on the setting element
		if (typeof pValue === 'boolean')
		{
			const tmpPanelBlock = document.getElementById(`setting-${pSetting}`);
			if (tmpPanelBlock)
			{
				if (pValue)
				{
					tmpPanelBlock.classList.add('is-active');
				}
				else
				{
					tmpPanelBlock.classList.remove('is-active');
				}
			}
			else
			{
				this.log.warn(`No panel block found for setting ${pSetting}`);
			}
		}
		// if the setting is a string, set the class on the setting element
		else if (typeof pValue === 'string')
		{
			// clear all classes on the setting element by getting a partial id match
			const tmpPanelBlocks = document.querySelectorAll(`[id^="setting-${pSetting}-"]`);
			tmpPanelBlocks.forEach((block) =>
			{
				block.classList.remove('is-active');
			});
			// set the class on the setting element
			const tmpPanelBlock = document.getElementById(`setting-${pSetting}-${pValue}`);
			if (tmpPanelBlock)
			{
				tmpPanelBlock.classList.add('is-active');
			}
			else
			{
				this.log.warn(`No panel block found for setting ${pSetting}-${pValue}`);
			}
		}
	}
}

module.exports = HeadlightPaneSettings;

module.exports.default_configuration = _ViewConfiguration;
